import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import HomePage from '@/pages/HomePage';
import PhraseDetailPage from '@/pages/PhraseDetailPage';
import FeedbackPage from '@/pages/FeedbackPage';
import DinoLlamaTimerPage from '@/pages/DinoLlamaTimerPage';
import TodoPage from '@/pages/TodoPage';
import AboutPage from '@/pages/AboutPage';
import FAQsPage from '@/pages/FAQsPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import AuthCallback from '@/pages/AuthCallback';
import AppleCallbackPage from '@/pages/AppleCallbackPage';
import ResourcesPage from '@/pages/ResourcesPage';
import ResourceManagerPage from '@/pages/ResourceManagerPage';
import SupportPage from '@/pages/SupportPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import SubscriptionSuccessPage from '@/pages/SubscriptionSuccessPage';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminTestEnvironment from '@/pages/AdminTestEnvironment';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SettingsPage from '@/pages/SettingsPage';
import JoinSubscriptionPage from '@/pages/JoinSubscriptionPage';
import GiftTrialPage from '@/pages/GiftTrialPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import FriendsPage from '@/pages/FriendsPage';
import ProfilePage from '@/pages/ProfilePage';
import ChallengesPage from '@/pages/ChallengesPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import SafetyStandardsPage from '@/pages/SafetyStandardsPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import BlogEditorPage from '@/pages/BlogEditorPage';
import ThoughtInterpreterPage from '@/pages/ThoughtInterpreterPage';
import LandingPage from '@/pages/LandingPage';
import ForOrganizationsPage from '@/pages/ForOrganizationsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AuthorPage from '@/pages/AuthorPage';
import ContributorsPage from '@/pages/ContributorsPage';
import PressKitPage from '@/pages/PressKitPage';
import CommunicationEmojisPage from '@/pages/CommunicationEmojisPage';
import { DetectiveDinoBot } from '@/components/DetectiveDinoBot';
// GscPingFab moved into the admin → GSC Indexing tab (was previously a
// global FAB but the ?fromApp=1 / preview environments were 401'ing on
// every page render).
import { BottomNav } from '@/components/BottomNav';
import SiteFooter from '@/components/SiteFooter';
import { Toaster } from '@/components/ui/sonner';
import { PhrasesProvider } from '@/context/PhrasesContext';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { FreePhraseProvider } from '@/context/FreePhraseContext';
import { GuestProvider } from '@/context/GuestContext';
import { VipUpgradeBanner } from '@/components/VipUpgradeBanner';
import { VipTrialOfferModal } from '@/components/VipTrialOfferModal';
import { StreakFreezeNotification } from '@/components/StreakFreezeNotification';
import { GuestRestricted } from '@/components/GuestRestricted';
import { ServiceWorkerUpdater } from '@/hooks/useServiceWorker';
import { useNotificationScheduler } from '@/hooks/useNotificationScheduler';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';
import useErrorLogger from '@/hooks/useErrorLogger';
import useReferralCapture, { attributeReferralIfAny } from '@/hooks/useReferralCapture';
import AdBanner from '@/components/AdBanner';
import FirstVisitTour from '@/components/FirstVisitTour';
import LibraryTour from '@/components/LibraryTour';

// Component to run notification scheduler
function NotificationScheduler() {
  useNotificationScheduler();
  return null;
}

// Component to handle trial offer modal
function TrialOfferHandler() {
  const { showTrialOffer, dismissTrialOffer } = useAuth();
  return (
    <VipTrialOfferModal 
      isOpen={showTrialOffer} 
      onClose={dismissTrialOffer} 
    />
  );
}

// Component to handle opening screen preference
function OpeningScreenRedirect() {
  const openingScreen = localStorage.getItem('clearcomm-opening-screen') || 'home';
  const location = useLocation();
  // Only redirect from exact "/home" path for users with non-home preference
  if (location.pathname === '/home' && !location.search && !location.hash) {
    if (openingScreen === 'todo') return <Navigate to="/todo" replace />;
    if (openingScreen === 'timer') return <Navigate to="/timer" replace />;
    if (openingScreen === 'library') return <Navigate to="/library" replace />;
    if (openingScreen === 'blog') return <Navigate to="/blog" replace />;
    if (openingScreen === 'interpreter') return <Navigate to="/interpreter" replace />;
    // 'home' is the default — let it render normally (no redirect)
  }
  return null;
}

// Legacy /account redirect — preserves ?welcome=1 query string so new
// signups (Google/Facebook/Apple) still trigger the welcome banner on
// ProfilePage. Anything else goes to /settings.
function AccountRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get('welcome') === '1') {
    return <Navigate to={`/profile?welcome=1`} replace />;
  }
  // "Account" should land on the public Profile page where the user
  // edits avatar, display name, bio, etc. Settings is for display
  // preferences (theme, language defaults) and is reached via the
  // explicit "Settings" link on /profile.
  return <Navigate to="/profile" replace />;
}

// Router component to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  useCanonicalUrl();
  useErrorLogger();
  useReferralCapture();

  // Auto-attribute any stored outlet ref code once the user signs in.
  // Runs once per auth transition. The helper is idempotent server-side
  // (first-touch attribution) and clears localStorage after one attempt.
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('auth_token');
    if (token) attributeReferralIfAny(token);
  }, [user]);
  
  // Listen for navigation messages from service worker (notification clicks)
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [navigate]);
  
  // Check URL fragment for session_id (Emergent-managed Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check URL query string for google_token (custom Google OAuth callback).
  // Set by /api/auth/google/callback after a successful sign-in. We stash
  // the JWT in localStorage under 'auth_token' (the SAME key AuthContext
  // reads) and clear the query string so it doesn't pollute back/forward
  // navigation.
  //
  // Why query string (not fragment): Cloudflare and many proxies strip
  // URL fragments on 301/302 redirects, which silently breaks Google
  // login on production. We use ?google_token= so the token survives the
  // apex/www redirect chain.
  if (location.search?.includes('google_token=')) {
    try {
      const params = new URLSearchParams(location.search);
      const tok = params.get('google_token');
      const isNew = params.get('newUser') === '1';
      if (tok) {
        localStorage.setItem('auth_token', tok);
        // Strip google_token + newUser from the URL and refresh AuthContext
        // via a full navigation so every hook picks up the new auth state.
        params.delete('google_token');
        params.delete('newUser');
        const remaining = params.toString();
        const cleanPath = location.pathname + (remaining ? `?${remaining}` : '');
        // New signups land on /profile?welcome=1 — matches Facebook/Apple flow.
        const target = isNew ? '/profile?welcome=1' : cleanPath;
        window.location.replace(target);
      }
    } catch { /* ignore */ }
  }
  
  // Pages where VIP banner should NOT show
  const hideBannerPages = ['/login', '/register', '/admin', '/subscription', '/account'];
  const showBanner = !hideBannerPages.some(page => location.pathname.startsWith(page));
  
  // Pages where bottom nav should show (for all users on main app pages)
  // BottomNav shown on key tool pages — explicitly excludes the linktree
  // home page '/' so the public landing experience stays focused on the
  // call-to-action pills.
  const showBottomNavPages = ['/library', '/leaderboard', '/friends', '/timer', '/todo', '/phrase', '/challenges', '/resources'];
  const showBottomNav = showBottomNavPages.some(page => 
    location.pathname === page || location.pathname.startsWith('/phrase/')
  );

  // Pages where the SiteFooter should NOT show. The blog page already
  // renders SiteFooter inline; admin/auth/subscription pages are kiosk-
  // style and would feel cluttered with a legal-links footer beneath
  // their own UI.
  const hideFooterPages = ['/admin', '/login', '/register', '/forgot-password', '/reset-password', '/auth', '/subscription', '/blog', '/account', '/leaderboard', '/profile', '/settings'];
  const showSiteFooter = !hideFooterPages.some(page => location.pathname.startsWith(page));
  
  return (
    <>
      {showBanner && <VipUpgradeBanner />}
      <TrialOfferHandler />
      <StreakFreezeNotification />
      <FirstVisitTour />
      <LibraryTour />
      <Routes>
        {/* Landing page at root */}
        <Route path="/" element={<><OpeningScreenRedirect /><LandingPage /></>} />
        <Route path="/for-organizations" element={<ForOrganizationsPage />} />
        <Route path="/library" element={<HomePage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        
        {/* Gaming/Social pages - require account */}
        {/* /statistics merged into /leaderboard — keep the URL working
            with a redirect so external links/bookmarks still resolve. */}
        <Route path="/statistics" element={<Navigate to="/leaderboard" replace />} />
        <Route path="/leaderboard" element={
          <GuestRestricted feature="leaderboard">
            <LeaderboardPage />
          </GuestRestricted>
        } />
        <Route path="/friends" element={
          <GuestRestricted feature="friends">
            <FriendsPage />
          </GuestRestricted>
        } />
        <Route path="/profile" element={
          <GuestRestricted feature="profile">
            <ProfilePage />
          </GuestRestricted>
        } />
        <Route path="/challenges" element={
          <GuestRestricted feature="challenges">
            <ChallengesPage />
          </GuestRestricted>
        } />
        <Route path="/settings" element={
          <GuestRestricted feature="settings">
            <SettingsPage />
          </GuestRestricted>
        } />
        
        {/* Phrase detail - accessible to guests */}
        <Route path="/phrase/:id" element={<PhraseDetailPage />} />
        
        {/* Free tools - accessible to guests */}
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/timer" element={<DinoLlamaTimerPage />} />
        <Route path="/todo" element={<TodoPage />} />
        <Route path="/interpreter" element={<ThoughtInterpreterPage />} />
        <Route path="/interpret" element={<Navigate to="/interpreter" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/press" element={<PressKitPage />} />
        <Route path="/communication-emojis" element={<CommunicationEmojisPage />} />
        <Route path="/faqs" element={<FAQsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/apple/callback" element={<AppleCallbackPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/test" element={<AdminTestEnvironment />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account" element={<AccountRedirect />} />
        <Route path="/join/:token" element={<JoinSubscriptionPage />} />
        <Route path="/gift/:token" element={<GiftTrialPage />} />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        {/* Alias /policy → /privacy: both URLs render the same
            PrivacyPolicyPage component (not a redirect). Google's
            OAuth verification crawler hits /policy directly, and
            some older marketing screenshots still link there. */}
        <Route path="/policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/safety" element={<SafetyStandardsPage />} />
        
        {/* Blog */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postId" element={<BlogPostPage />} />
        <Route path="/blog/admin" element={<BlogEditorPage />} />
        <Route path="/blog/admin/edit/:postId" element={<BlogEditorPage />} />
        <Route path="/author/:slug" element={<AuthorPage />} />
        <Route path="/contributors" element={<ContributorsPage />} />

        {/* Resources directory — admin-curated external links */}
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/manage" element={<ResourceManagerPage />} />

        {/* "Support us" linktree — payments routed via external browser
            when `?fromApp=1` is set so we stay store-policy-compliant. */}
        <Route path="/support" element={<SupportPage />} />
        {/* Legacy /donate alias — keeps any older marketing links alive */}
        <Route path="/donate" element={<SupportPage />} />
        {/* 404 catch-all — must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {showSiteFooter && <SiteFooter />}
      {showBottomNav && <BottomNav />}
      <DetectiveDinoBot />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <GuestProvider>
          <LanguageProvider>
            <PhrasesProvider>
              <SubscriptionProvider>
                <FreePhraseProvider>
                  <BrowserRouter>
                    <AdBanner />
                    <div className="w-full max-w-6xl mx-auto">
                      <AppRouter />
                    </div>
                  </BrowserRouter>
                  <Toaster />
                  <ServiceWorkerUpdater />
                  <NotificationScheduler />
                </FreePhraseProvider>
              </SubscriptionProvider>
            </PhrasesProvider>
          </LanguageProvider>
        </GuestProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
